'use client';

/** Imports */
import {useEffect, useRef, useState, type ReactNode} from 'react';
import {useSession} from 'next-auth/react';
import {type AppRouter} from '@/server/api/root';
import {makeQueryClient, retireQueryClient} from '@/trpc/query-client';
import {QueryClientProvider} from '@tanstack/react-query';
import {createTRPCClient, loggerLink} from '@trpc/client';
import {createTRPCContext} from '@trpc/tanstack-react-query';
import {createDashboardTransportLink} from '@/trpc/dashboard-transport';


/** Types */
type Props = Readonly<{
    children: ReactNode;
}>;


/**
 * The URL of the tRPC API endpoint.
 * This is relative to the current origin, so it will work in development and production without needing to change it.
 */
const TRPC_API_URL = '/api/trpc';
export const {TRPCProvider, useTRPC, useTRPCClient} = createTRPCContext<AppRouter>();


/**
 * The TRPCReactProvider component sets up the tRPC client and provides it to the rest of the app.
 *
 * @param props - The props for the component, which include the children to render inside the provider.
 * @returns A React component that provides the tRPC client to its children.
 */
export function TRPCReactProvider(props: Props) {
    const {data: session} = useSession();
    return <IdentityTRPCProvider key={session?.user?.id ?? 'anonymous'}>{props.children}</IdentityTRPCProvider>;
}

/** A keyed mount owns its cache; speculative identity renders never mutate the committed cache. */
function IdentityTRPCProvider({children}: Props) {
    const [queryClient] = useState(makeQueryClient);
    const lifecycle = useRef({generation: 0});
    useEffect(() => {
        const state = lifecycle.current;
        const generation = ++state.generation;
        return () => {
            // Strict Mode replays effects on a live mount. Retire only if setup did not resume.
            queueMicrotask(() => {
                if (state.generation === generation) void retireQueryClient(queryClient);
            });
        };
    }, [queryClient]);

    // State
    const [trpcClient] = useState(() => createTRPCClient<AppRouter>({
        links: [
            loggerLink({enabled: () => (process.env.NODE_ENV === 'development')}),
            createDashboardTransportLink({url: TRPC_API_URL}),
        ],
    }));


    // Render
    return (
        <QueryClientProvider client={queryClient}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
                {children}
            </TRPCProvider>
        </QueryClientProvider>
    );
}
